import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import { useDistributorScope } from '../hooks/useDistributorScope';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton, FieldText, FieldSelect, useIsViewMode,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

/**
 * NamedDefinitionPage'in tam-sayfa karşılığı — iki alanlı (kod + açıklama/ad)
 * "tanım" kaydının /entity/new ve /entity/:id detay ekranı.
 * NamedDefinitionPage ile AYNI config props'larını alır; aynı backend uçlarını
 * kullanır (Add/Update/Delete + GetById, yoksa listeden fallback).
 *
 * Props:
 *   apiBase, title, listRoute
 *   codeField/codeLabel/codeRequired   — 1. alan (varsayılan textId/Kod/zorunlu)
 *   descField/descriptionLabel/descRequired — 2. alan (varsayılan description/Açıklama/opsiyonel)
 *   entityLabel — silme/mesajlarda kullanılacak ("kayıt")
 *   withVendor  — #829 opsiyonel "Distribütör" alanını açar. Bu bileşen ~onlarca tanım
 *                 ekranı tarafından paylaşıldığı için alan OPT-IN'dir: yalnız vendorId
 *                 taşıyan entity'lerde (şu an Üretici) açılmalıdır.
 */
function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }
function cap(field) { return field ? field.charAt(0).toUpperCase() + field.slice(1) : field; }

export function NamedDefinitionDetailPage({
  apiBase,
  title,
  listRoute,
  codeField = 'textId',
  codeLabel = 'Kod',
  codeRequired = true,
  descField = 'description',
  descriptionLabel = 'Açıklama',
  descRequired = false,
  entityLabel = 'kayıt',
  // Değişiklik geçmişi: backend ChangeHistoryRegistry'deki mantıksal entity adı (ör. "Producer").
  // Verilirse detay başlığında geçmiş (⟳) butonu + zaman çizelgesi çekmecesi görünür.
  historyEntity,
  withVendor = false,
}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const isView = useIsViewMode() && !isNew; // #282 izle = salt-okunur
  const { canAdd, canEdit, canDelete, canCopy } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? `Yeni ${title}` : `${title} Detayı`, 'Kod ve açıklama tanımı.');

  // #829 withVendor açıksa formda vendorId de taşınır; boş = tüm distribütörler için geçerli tanım.
  const EMPTY_FORM = { [codeField]: '', [descField]: '', ...(withVendor ? { vendorId: '' } : {}) };

  // #829 Distribütör kapsamı — seçenekler kullanıcının yetkili olduğu distribütörler.
  const { vendorOptions, lockedOptions, isDistributorUser, lockedVendorId, scopeLoaded } = useDistributorScope({ enabled: withVendor });

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  const rowToForm = useCallback((r) => ({
    [codeField]: pick(r, codeField, cap(codeField)) ?? '',
    [descField]: pick(r, descField, cap(descField)) ?? '',
    ...(withVendor ? { vendorId: pick(r, 'vendorId', 'VendorId') ?? '' } : {}),
  }), [codeField, descField, withVendor]);

  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    // 1) location.state.row önceliği
    if (location.state?.row) {
      const sr = location.state.row;
      setForm(rowToForm(sr));
      // Nav-state satırı liste OData $select'inden gelir; audit alanlarını (createdDate/By…)
      // İÇERMEZ → info butonu gizli kalır. Tam kaydı arka planda çekip (GetById, yoksa Select
      // fallback) audit'i ondan doldur (paint'i bloklamaz).
      (async () => {
        try {
          let rec = null;
          try {
            const res = await fetch(`${apiBase}/GetById?Id=${id}`, { headers: authHeaders() });
            if (res.ok) rec = unwrap(await res.json());
          } catch { /* GetById yoksa listeye düş */ }
          if (!rec) {
            const res = await fetch(`${apiBase}/Select`, { headers: authHeaders() });
            if (res.ok) {
              const items = unwrap(await res.json());
              rec = (Array.isArray(items) ? items : []).find((x) => String(pick(x, 'id', 'Id')) === String(id));
            }
          }
          if (rec) setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
        } catch { /* audit best-effort */ }
      })();
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // 2) GetById?Id=<guid>
      let r = null;
      try {
        const res = await fetch(`${apiBase}/GetById?Id=${id}`, { headers: authHeaders() });
        if (res.ok) r = unwrap(await res.json());
      } catch { /* GetById yoksa listeye düş */ }
      // 3) Liste fallback (Select) + find
      if (!r) {
        const res = await fetch(`${apiBase}/Select`, { headers: authHeaders() });
        if (!res.ok) throw new Error(`Kayıt alınamadı (${res.status})`);
        const items = unwrap(await res.json());
        r = (Array.isArray(items) ? items : []).find((x) => String(pick(x, 'id', 'Id')) === String(id));
        if (!r) throw new Error('Kayıt bulunamadı');
      }
      setForm(rowToForm(r));
      setAudit({ createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(r, 'createdDate', 'CreatedDate'), updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    } catch (err) {
      showToast('error', err.message || 'Kayıt alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew, apiBase, rowToForm]);

  useEffect(() => { load(); }, [load]);

  // #829 Distribütör tipi kullanıcı: yetkili distribütör otomatik seçili + DEĞİŞTİRİLEMEZ.
  // load() bittikten sonra çalışır; aksi halde kaydın kendi vendorId'si oto-seçimi ezerdi.
  useEffect(() => {
    if (!withVendor || !scopeLoaded || loading || !lockedVendorId) return;
    setForm((c) => (String(c.vendorId ?? '') === lockedVendorId ? c : { ...c, vendorId: lockedVendorId }));
  }, [withVendor, scopeLoaded, loading, lockedVendorId]);

  // Klonla: /entity/new'e kopya veriyle gelindiyse formu doldur (Kaydet'e kadar kaydetme yok).
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp boş yeni kayda hazırlan.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (codeRequired && !String(form[codeField] ?? '').trim()) { showToast('error', `${codeLabel} boş olamaz`); return; }
    if (descRequired && !String(form[descField] ?? '').trim()) { showToast('error', `${descriptionLabel} boş olamaz`); return; }
    setSaving(true);
    try {
      const payload = {
        [codeField]: form[codeField],
        [descField]: form[descField],
        // #829 Boş distribütör = tüm distribütörler için geçerli → backend'e null gider ('' değil).
        ...(withVendor ? { vendorId: form.vendorId || null } : {}),
      };
      const res = await fetch(`${apiBase}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(isNew ? payload : { id, ...payload }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || (isNew ? 'Kayıt eklenemedi' : 'Kayıt güncellenemedi'));
      if (isNew) {
        if (andNew) {
          showToast('success', `${title} eklendi. Yeni kayda devam edebilirsiniz.`);
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', `${title} eklendi.`);
        setTimeout(() => navigate(listRoute), 400);
        return;
      }
      showToast('success', `${title} güncellendi.`);
      setTimeout(() => navigate(listRoute), 400);
    } catch (err) {
      showToast('error', err.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut form bilgilerini taşıyarak yeni-kayıt ekranını aç (kaydetmeden).
  const handleClone = () => {
    navigate(`${listRoute}/new`, { state: { clone: { ...form } } });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${apiBase}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || 'Kayıt silinemedi');
      showToast('success', `${title} silindi.`);
      setTimeout(() => navigate(listRoute), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  // #282 İzle modunda salt-okunur: yalnız "Kapat" + "Düzenle".
  const actions = isView ? (
    <>
      <button className="dps-btn ghost" onClick={() => navigate(listRoute)}>Kapat</button>
      <button className="dps-btn primary" onClick={() => navigate(`${listRoute}/${id}`, { state: { row: form, view: false }, replace: true })}>Düzenle</button>
    </>
  ) : (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate(listRoute)} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni kayıt aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      readOnly={isView}
      crumb={[{ label: title, to: listRoute }, { label: isNew ? 'Yeni' : (isView ? 'Görüntüle' : 'Detay'), current: true }]}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew || !historyEntity ? null : { entityName: historyEntity, entityId: id, title: `${title} Değişiklik Geçmişi` }}
      loading={loading}
    >
      <div className="dps-form">
        <Section icon="list" title={title} subtitle={withVendor ? 'Kod, açıklama ve distribütör kapsamı' : 'Kod ve açıklama bilgileri'}>
          <Grid cols={2}>
            <FieldText form={form} set={set} tried={tried} k={codeField} label={codeLabel} req={codeRequired} mono />
            <FieldText form={form} set={set} tried={tried} k={descField} label={descriptionLabel} req={descRequired} />
            {/* #829 Distribütör — zorunlu değil. Boş bırakılırsa tanım tüm distribütörler için
                geçerlidir; seçilirse yalnız seçili distribütör için. Distribütör tipi kullanıcıda
                yetkili distribütör otomatik seçili ve değiştirilemez. */}
            {withVendor && (
              <FieldSelect
                form={form} set={set} k="vendorId" label="Distribütör"
                options={isDistributorUser && lockedVendorId ? lockedOptions : vendorOptions}
                disabled={Boolean(isDistributorUser && lockedVendorId)}
                hint={isDistributorUser && lockedVendorId
                  ? 'Distribütör tipi kullanıcı — yetkili distribütörünüz otomatik seçildi.'
                  : 'Boş bırakılırsa tüm distribütörler için geçerli olur.'}
              />
            )}
          </Grid>
        </Section>
      </div>

      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form[descField] || form[codeField]}”</strong> {entityLabel}ını silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default NamedDefinitionDetailPage;
